import 'isomorphic-fetch';

import { nanoid } from 'nanoid/non-secure';
import stringify from 'qs/lib/stringify';

import { Method, RequestHeaders, RequestOptions, InternalSession, Session } from '../types';
import { getOptions } from './options';
import { RECHARGE_API_URL, RECHARGE_CDN_URL, SHOPIFY_APP_PROXY_URL } from '../constants/api';
import { RechargeRequestError } from './error';

function stringifyQuery(str: unknown): string {
  return stringify(str, {
    encode: false,
    indices: false,
    arrayFormat: 'comma',
  });
}

/** @internal */
export function getInternalSession(session: Session | InternalSession, fnName: string): InternalSession {
  return {
    ...session,
    internalFnCall: (session as InternalSession).internalFnCall ?? fnName,
    internalRequestId: (session as InternalSession).internalRequestId ?? nanoid(),
  };
}

export async function cdnRequest<T>(method: Method, url: string, requestOptions: RequestOptions = {}): Promise<T> {
  const opts = getOptions();
  return request(method, `${RECHARGE_CDN_URL(opts.environment)}/store/${opts.storeIdentifier}${url}`, requestOptions);
}

export async function rechargeApiRequest<T>(
  method: Method,
  url: string,
  { id, query, data, headers }: RequestOptions = {},
  session: InternalSession
): Promise<T> {
  const {
    environment,
    environmentUri,
    storeIdentifier,
    loginRetryFn,
    __unstable_twoFactorRetryFn,
    appName,
    appVersion,
  } = getOptions();
  if (!storeIdentifier) {
    throw new Error('InitRecharge has not been called and/or no store identifier has been defined.');
  }
  const token = session.apiToken;
  if (!token) {
    throw new Error('No API token defined on session.');
  }
  const rechargeBaseUrl = RECHARGE_API_URL(environment, environmentUri);
  const reqHeaders: RequestHeaders = {
    'X-Recharge-Access-Token': token,
    'X-Recharge-Version': '2021-11',
    'X-Recharge-Sdk-Fn': session.internalFnCall,
    ...(appName ? { 'X-Recharge-Sdk-App-Name': appName } : {}),
    ...(appVersion ? { 'X-Recharge-Sdk-App-Version': appVersion } : {}),
    'X-Recharge-Sdk-Version': '__SDK_VERSION__',
    'X-Request-Id': session.internalRequestId,
    ...(session.tmp_fn_identifier ? { 'X-Recharge-Sdk-Fn-Identifier': session.tmp_fn_identifier } : {}),
    ...(headers ? headers : {}),
  };

  const localQuery = {
    shop_url: storeIdentifier,
    ...(query as any),
  };

  try {
    // await to catch any errors
    return await request<T>(method, `${rechargeBaseUrl}${url}`, { id, query: localQuery, data, headers: reqHeaders });
  } catch (error: unknown) {
    if (loginRetryFn && error instanceof RechargeRequestError && error.status === 401) {
      // call loginRetryFn and retry request on 401
      return loginRetryFn().then(session => {
        if (session) {
          return request(method, `${rechargeBaseUrl}${url}`, {
            id,
            query: localQuery,
            data,
            headers: {
              ...reqHeaders,
              'X-Recharge-Access-Token': session.apiToken,
            },
          });
        }
        throw error;
      });
    } else if (__unstable_twoFactorRetryFn && error instanceof RechargeRequestError && error.status === 423) {
      // trigger two-factor authentication flow and retry request on 423
      return __unstable_twoFactorRetryFn().then(session => {
        if (session) {
          return request(method, `${rechargeBaseUrl}${url}`, {
            id,
            query: localQuery,
            data,
            headers: {
              ...reqHeaders,
              'X-Recharge-Access-Token': session.apiToken,
            },
          });
        }
        throw error;
      });
    }
    throw error;
  }
}

export async function shopifyAppProxyRequest<T>(
  method: Method,
  url: string,
  requestOptions: RequestOptions = {}
): Promise<T> {
  return request(method, `${SHOPIFY_APP_PROXY_URL}${url}`, requestOptions);
}

export async function request<T>(
  method: Method,
  url: string,
  { id, query, data, headers }: RequestOptions = {}
): Promise<T> {
  let reqUrl = url.trim();

  if (id) {
    reqUrl = [reqUrl, `${id}`.trim()].join('/');
  }

  if (query) {
    let exQuery;
    [reqUrl, exQuery] = reqUrl.split('?');
    const fullQuery = [exQuery, stringifyQuery(query)].join('&').replace(/^&/, '');
    reqUrl = `${reqUrl}${fullQuery ? `?${fullQuery}` : ''}`;
  }

  let reqBody;
  if (data && method !== 'get') {
    reqBody = JSON.stringify(data);
  }

  const reqHeaders: RequestHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Recharge-App': 'storefront-client',
    ...(headers ? headers : {}),
  };

  const response = await fetch(reqUrl, {
    method,
    headers: reqHeaders,
    body: reqBody,
  });

  let result;
  try {
    result = await response.json();
  } catch (e) {
    // If we get here, it means we were a no content response.
  }

  if (!response.ok) {
    if (response.status === 502 || response.status === 504) {
      throw new RechargeRequestError('A gateway error occurred while making the request', response.status);
    } else if (result && result.error) {
      throw new RechargeRequestError(result.error, response.status);
    } else if (result && result.errors) {
      throw new RechargeRequestError(JSON.stringify(result.errors), response.status);
    } else {
      throw new RechargeRequestError('A connection error occurred while making the request');
    }
  }

  return result as T;
}
